import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Mail, Loader2, Inbox, RefreshCw, AlertCircle, CheckCircle2, Stethoscope } from "lucide-react";
import api from "@/lib/api";
import RTReadinessPanel from "@/components/RTReadinessPanel";

const inp = "w-full bg-muted/40 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50";

export default function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inboxStatus, setInboxStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);

  useEffect(() => {
    api.get("/office365/config").then((r) => setCfg(r.data)).catch((e) => {
      console.error("[EmailSettings] Failed to load O365 config", e);
      toast.error("Não foi possível carregar a configuração do Office 365.");
      // Fallback vazio para permitir render da página em vez de blank screen.
      setCfg({});
    });
    api.get("/emails/logs").then((r) => setLogs(Array.isArray(r.data) ? r.data : [])).catch((e) => {
      console.error("[EmailSettings] Failed to load email logs", e);
      setLogs([]);
    });
    api.get("/inbox-sync/status").then((r) => setInboxStatus(r.data)).catch(() => {});
  }, []);

  const [backfilling, setBackfilling] = useState(false);
  const [backfill, setBackfill] = useState(null);
  const backfillRtAttendees = async () => {
    setBackfilling(true);
    try {
      await api.put("/office365/config", cfg);
      const r = await api.post("/office365/rt-extra-attendees/backfill");
      setBackfill(r.data);
      toast.success(`RTs actualizadas: ${r.data.updated} · já tinham: ${r.data.already} · erros: ${r.data.errors.length}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao actualizar RTs");
    } finally {
      setBackfilling(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/office365/config", cfg);
      toast.success("Configuração Office 365 guardada");
      const s = await api.get("/inbox-sync/status");
      setInboxStatus(s.data);
    }
    catch(e){ toast.error("Erro"); } finally { setSaving(false); }
  };

  const triggerInboxSync = async () => {
    setSyncing(true);
    try {
      // Auto-save the config first so ``inbox_enabled`` + ``inbox_mailbox``
      // are persisted before Graph is contacted. Without this the backend
      // reads the OLD (unchanged) config and returns
      // ``skipped: inbox_disabled`` even though the checkbox is ticked in
      // the UI.
      await api.put("/office365/config", cfg);
      const r = await api.post("/inbox-sync/trigger");
      if (r.data?.ok) {
        toast.success(`Sync OK · ${r.data.matched} email(s) associados a leads · ${r.data.ignored} ignorados`);
      } else if (r.data?.skipped) {
        const map = {
          "o365_disabled": "Active primeiro o Office 365 no topo da página.",
          "inbox_disabled": "Marque 'Sincronização activa' antes de sincronizar.",
          "no_mailbox_configured": "Preencha a caixa (ex.: leads@caring.pt).",
        };
        toast.info(map[r.data.skipped] || `Sync não executado: ${r.data.skipped}`);
      } else if (r.data?.error) {
        toast.error(`Erro Graph: ${r.data.error}`);
      }
      const s = await api.get("/inbox-sync/status");
      setInboxStatus(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no sync");
    } finally {
      setSyncing(false);
    }
  };

  if (!cfg) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-6" data-testid="email-settings-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Integração · Email</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Office 365</h1>
        <p className="text-muted-foreground text-sm mt-2">Envio automático de estimativas, propostas e notificações via Microsoft Graph API.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Tenant ID</div>
            <input data-testid="o365-tenant" className={inp} value={cfg.tenant_id||""} onChange={(e)=>setCfg({...cfg, tenant_id:e.target.value})} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client ID</div>
            <input data-testid="o365-client-id" className={inp} value={cfg.client_id||""} onChange={(e)=>setCfg({...cfg, client_id:e.target.value})} />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client Secret</div>
            <input data-testid="o365-secret" type="password" className={inp} value={cfg.client_secret||""} onChange={(e)=>setCfg({...cfg, client_secret:e.target.value})} />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Email do remetente</div>
            <input data-testid="o365-sender" type="email" placeholder="geral@caring.pt" className={inp} value={cfg.sender_email||""} onChange={(e)=>setCfg({...cfg, sender_email:e.target.value})} />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Nome visível ao cliente (From)</div>
            <input data-testid="o365-sender-name" placeholder="CARING - Apoio ao Cliente" className={inp} value={cfg.sender_name||""} onChange={(e)=>setCfg({...cfg, sender_name:e.target.value})} />
            <div className="text-[11px] text-muted-foreground mt-1.5">O nome mostrado ao cliente na caixa de entrada. Ex: <code className="bg-muted px-1 rounded">CARING - SAC &lt;sac@caring.pt&gt;</code></div>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Reply-To (respostas dos clientes)</div>
            <input data-testid="o365-reply-to" type="email" placeholder="sac@caring.pt" className={inp} value={cfg.reply_to_email ?? "sac@caring.pt"} onChange={(e)=>setCfg({...cfg, reply_to_email:e.target.value})} />
            <div className="text-[11px] text-muted-foreground mt-1.5">Todos os emails enviados pelo Caring Estimativas (pela conta pessoal de cada utilizador) usam este endereço como Reply-To. Quando o cliente carregar em "Responder", a mensagem vai para a caixa central do SAC, e não para o email pessoal do gestor.</div>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Email do Recrutamento (RH)</div>
            <input data-testid="o365-hr-email" type="email" placeholder="workforce@caring.pt" className={inp} value={cfg.hr_email ?? "workforce@caring.pt"} onChange={(e)=>setCfg({...cfg, hr_email:e.target.value})} />
            <div className="text-[11px] text-muted-foreground mt-1.5">Destino das <b>requisições de recrutamento</b> geradas a partir de leads com caução confirmada. Quando o Gestor clicar em "Enviar ao RH", a mensagem é entregue neste endereço com o PDF anexado.</div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Participantes extra nas Reuniões Técnicas</div>
            <input
              data-testid="o365-rt-extra-attendees"
              placeholder="caring@caring.pt"
              className={inp}
              value={(cfg.rt_extra_attendees ?? ["caring@caring.pt"]).join(", ")}
              onChange={(e) => setCfg({ ...cfg, rt_extra_attendees: e.target.value.split(/[,;\s]+/).filter(Boolean) })}
            />
            <div className="text-[11px] text-muted-foreground mt-1.5">Endereços internos adicionados como <b>participantes opcionais</b> em <b>todos</b> os convites Teams das RTs (separados por vírgula). Deixe vazio para desactivar.</div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">A partir de (data da reunião)</div>
            <input
              data-testid="o365-rt-extra-attendees-from"
              type="date"
              className={inp}
              value={cfg.rt_extra_attendees_from ?? "2026-10-13"}
              onChange={(e) => setCfg({ ...cfg, rt_extra_attendees_from: e.target.value })}
            />
            <div className="text-[11px] text-muted-foreground mt-1.5">Só reuniões marcadas para esta data ou depois incluem os participantes extra.</div>
          </label>
          <div className="md:col-span-2 flex items-center gap-3 flex-wrap" data-testid="o365-rt-backfill-block">
            <button
              type="button"
              data-testid="o365-rt-backfill"
              onClick={backfillRtAttendees}
              disabled={backfilling}
              className="inline-flex items-center gap-2 bg-background border border-border hover:bg-accent text-foreground font-semibold rounded-full px-4 py-2 text-xs disabled:opacity-50"
            >
              {backfilling ? "A actualizar…" : "Actualizar RTs já agendadas"}
            </button>
            <span className="text-[11px] text-muted-foreground">
              Junta os participantes extra (opcionais) aos convites Teams já existentes a partir da data indicada — se já lá estiverem como obrigatórios, passam a opcionais (o Outlook envia a actualização automaticamente).
            </span>
            {backfill && (
              <span className="text-[11px] text-foreground" data-testid="o365-rt-backfill-result">
                {backfill.scanned} RT(s) analisadas · {backfill.updated} actualizadas · {backfill.already} já tinham · {backfill.not_found + backfill.cancelled} removidas/canceladas · {backfill.errors.length} erro(s)
              </span>
            )}
          </div>
        </div>
        <label className="flex items-center gap-3">
          <input data-testid="o365-enabled" type="checkbox" checked={cfg.enabled} onChange={(e)=>setCfg({...cfg, enabled:e.target.checked})} className="accent-primary" />
          <span className="text-sm">Ativar envio real (caso contrário, emails ficam apenas no log)</span>
        </label>
        <div className="bg-muted/70 border border-border rounded-lg p-4 text-xs text-muted-foreground leading-relaxed space-y-2">
          <div><b>Como obter:</b> Portal Azure → Azure Active Directory → App registrations → Nova aplicação → criar Client Secret → conceder consentimento admin.</div>
          <div>
            <b>Permissões (Application) necessárias:</b>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li><code>Mail.Send</code> — envio de emails (estimativas, propostas, notificações).</li>
              <li><code>Calendars.ReadWrite</code> — agendar Reunião Técnica no Outlook do gestor + criar link Microsoft Teams automaticamente.</li>
              <li><code>Mail.Read</code> + <code>Mail.ReadWrite</code> <span className="text-muted-foreground">(necessárias para sync da caixa <code>leads@caring.pt</code>)</span> — ler emails recebidos, marcar como lidos e aplicar categorias.</li>
              <li><code>MailboxSettings.Read</code> <span className="text-muted-foreground">(opcional)</span> — ler timezone da caixa do gestor.</li>
            </ul>
            <div className="mt-2 text-[11px] text-muted-foreground">Sem <code>Calendars.ReadWrite</code>, o agendamento de reuniões Teams falha com erro 403. Peça <b>Grant admin consent</b> depois de adicionar a permissão.</div>
          </div>
        </div>
        <button data-testid="o365-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-6 py-3 text-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </button>
      </div>

      {/* ---------- INBOX SYNC (leads@caring.pt) ---------- */}
      <div className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="inbox-sync-section">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-foreground" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-display text-2xl">Sincronização da caixa de leads</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Emails recebidos são associados ao lead correspondente (por email do remetente) e sincronizados com o Zoho.</p>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="inbox-enabled"
            checked={!!cfg.inbox_enabled}
            onChange={(e) => setCfg({ ...cfg, inbox_enabled: e.target.checked })}
            className="mt-1 accent-primary"
          />
          <div>
            <div className="text-sm font-semibold text-foreground">Sincronização activa</div>
            <div className="text-xs text-muted-foreground">Sonda a caixa a cada 3 minutos, marca emails como lidos e aplica categorias <code className="bg-muted px-1 rounded">Zoho: &lt;status&gt;</code> + <code className="bg-muted px-1 rounded">Op: &lt;utilizador&gt;</code>.</div>
          </div>
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Caixa a sincronizar</div>
          <input
            type="email"
            data-testid="inbox-mailbox"
            placeholder="leads@caring.pt"
            className={inp}
            value={cfg.inbox_mailbox || ""}
            onChange={(e) => setCfg({ ...cfg, inbox_mailbox: e.target.value })}
          />
          <div className="text-[11px] text-muted-foreground mt-1.5">
            Requer permissões <b>Application</b> <code>Mail.Read</code> + <code>Mail.ReadWrite</code> concedidas com <b>admin consent</b>. Recomenda-se restringir com uma <i>application access policy</i> a esta única caixa.
          </div>
        </label>

        {/* 20/02/2026 — Secondary mailbox: sac.qi@caring.pt. Same engine,
            independent watermark. Client replies to "tentativa de
            contacto" outreach emails are attached to the matched lead. */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
            Caixa secundária — Serviço Apoio ao Cliente
          </div>
          <label className="flex items-start gap-3 cursor-pointer mb-3">
            <input
              type="checkbox"
              data-testid="sac-qi-enabled"
              checked={cfg.sac_qi_enabled !== false}
              onChange={(e) => setCfg({ ...cfg, sac_qi_enabled: e.target.checked })}
              className="mt-1 accent-primary"
            />
            <div>
              <div className="text-sm font-semibold text-foreground">Sincronizar sac.qi@caring.pt</div>
              <div className="text-xs text-muted-foreground">Respostas dos clientes aos emails de tentativa de contacto são associadas automaticamente ao lead pelo endereço de origem.</div>
            </div>
          </label>
          <input
            type="email"
            data-testid="sac-qi-mailbox"
            placeholder="sac.qi@caring.pt"
            className={inp}
            value={cfg.sac_qi_mailbox || ""}
            onChange={(e) => setCfg({ ...cfg, sac_qi_mailbox: e.target.value })}
          />
          {inboxStatus?.sac_qi_last_sync_at && (
            <div className="text-[11px] text-muted-foreground mt-1.5">
              Último sync: <b>{new Date(inboxStatus.sac_qi_last_sync_at).toLocaleString("pt-PT")}</b>
              {" · "}emails associados: <b>{inboxStatus.sac_qi_last_sync_count || 0}</b>
              {inboxStatus.sac_qi_last_error && (
                <span className="text-[hsl(var(--danger))]"> · erro: {inboxStatus.sac_qi_last_error}</span>
              )}
            </div>
          )}
        </div>

        {inboxStatus && (
          <div className="bg-muted/40 border border-border rounded-lg p-4 text-xs space-y-1.5" data-testid="inbox-status">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Estado</span>
              <span className={`font-mono ${inboxStatus.inbox_enabled ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                {inboxStatus.inbox_enabled ? "ACTIVO" : "INACTIVO"}
              </span>
            </div>
            {inboxStatus.last_sync_at && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Último sync</span>
                  <span className="text-foreground">{new Date(inboxStatus.last_sync_at).toLocaleString("pt-PT")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Emails associados neste sync</span>
                  <span className="text-foreground font-semibold">{inboxStatus.last_sync_count}</span>
                </div>
              </>
            )}
            {inboxStatus.last_error && (
              <div className="flex items-start gap-2 pt-2 border-t border-border">
                <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                <span className="text-[hsl(var(--danger))] font-mono text-[11px] break-all">{inboxStatus.last_error}</span>
              </div>
            )}
            {!inboxStatus.last_error && inboxStatus.last_sync_at && (
              <div className="flex items-start gap-2 pt-2 border-t border-border">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-[hsl(var(--success))]">Sem erros no último ciclo.</span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={triggerInboxSync}
            disabled={syncing || !cfg.enabled || !cfg.inbox_enabled}
            data-testid="inbox-trigger"
            className="inline-flex items-center gap-2 bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-5 py-2 text-sm font-medium disabled:opacity-40"
            title={!cfg.enabled ? "Active o Office 365 primeiro" : (!cfg.inbox_enabled ? "Active a sincronização e guarde primeiro" : "Sincronizar agora")}
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sincronizar agora
          </button>
          <button
            onClick={async () => {
              setDiagnosing(true);
              setDiagnostic(null);
              try {
                const r = await api.get("/inbox-sync/diagnose?days_back=30&limit=50");
                setDiagnostic(r.data);
              } catch (e) {
                toast.error(e.response?.data?.detail || e.message || "Erro no diagnóstico");
              } finally {
                setDiagnosing(false);
              }
            }}
            disabled={diagnosing}
            data-testid="inbox-diagnose"
            className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border text-foreground rounded-full px-5 py-2 text-sm font-medium disabled:opacity-40"
            title="Inspeccionar a caixa e mostrar porque cada email é ou não sincronizado (últimos 30 dias, ignora isRead)"
          >
            {diagnosing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
            Diagnosticar
          </button>
        </div>

        {diagnostic && (
          <div className="mt-4 border border-border bg-muted/40 rounded-xl p-5 space-y-4 text-xs" data-testid="diagnostic-result">
            <div className="flex items-center justify-between">
              <div className="font-ui text-sm text-foreground flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-foreground" /> Diagnóstico da caixa <b>{diagnostic.config?.inbox_mailbox || "(sem mailbox)"}</b>
              </div>
              <button
                onClick={() => setDiagnostic(null)}
                className="text-muted-foreground hover:text-foreground text-[11px]"
              >Fechar</button>
            </div>

            {/* Health flags */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                ["Office 365", diagnostic.config?.enabled],
                ["Inbox Sync", diagnostic.config?.inbox_enabled],
                ["Token OAuth", diagnostic.token_ok],
                ["Caixa acessível", diagnostic.mailbox_ok],
              ].map(([label, ok]) => (
                <div key={label} className={`rounded-lg p-2.5 border ${ok ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25 text-[hsl(var(--success))]" : "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))]"}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
                  <div className="font-mono font-bold text-sm">{ok ? "OK" : "FALHA"}</div>
                </div>
              ))}
            </div>

            {/* Blocking reasons */}
            {Array.isArray(diagnostic.reasons) && diagnostic.reasons.length > 0 && (
              <div className="bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded-lg p-3 space-y-1">
                <div className="text-[hsl(var(--danger))] font-semibold text-[11px] uppercase tracking-widest">Motivos de bloqueio</div>
                {diagnostic.reasons.map((r, i) => (
                  <div key={i} className="text-[hsl(var(--danger))] leading-relaxed" data-testid={`diag-reason-${i}`}>• {r}</div>
                ))}
              </div>
            )}

            {/* Lead-email stats */}
            {diagnostic.lead_email_stats && (
              <div className="text-muted-foreground">
                <b>{diagnostic.lead_email_stats.with_email}</b>/{diagnostic.lead_email_stats.total_leads} leads têm email de contacto preenchido.
                {diagnostic.lead_email_stats.with_email === 0 && (
                  <div className="text-[hsl(var(--danger))] mt-1">
                    ⚠️ Nenhum lead tem email de contacto — a sincronização não consegue fazer match. Confirme que o campo <code>qualification.contact.email</code> está a ser preenchido nas qualificações.
                  </div>
                )}
              </div>
            )}

            {/* Summary */}
            {diagnostic.summary && (
              <div className="text-muted-foreground">
                <b>{diagnostic.summary.shown}</b> mensagens exibidas de <b>{diagnostic.summary.fetched}</b> devolvidas pela caixa nos últimos {diagnostic.summary.days_back} dias · <b>{diagnostic.summary.unique_senders}</b> remetentes únicos.
              </div>
            )}

            {/* Per-message verdicts */}
            {Array.isArray(diagnostic.messages) && diagnostic.messages.length > 0 && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="grid grid-cols-[110px_1fr_120px_120px] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground bg-card px-3 py-2 border-b border-border">
                  <div>Data</div>
                  <div>Remetente / Assunto</div>
                  <div>isRead</div>
                  <div>Verdicto</div>
                </div>
                {diagnostic.messages.map((m, i) => {
                  const verdictStyle = {
                    matched: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25",
                    no_lead_with_this_email: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25",
                    self: "text-foreground bg-muted border-border",
                    no_sender: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25",
                  }[m.verdict] || "text-muted-foreground bg-card border-border";
                  const verdictLabel = {
                    matched: `✔ Lead ${m.matched_lead_name || m.matched_lead_id?.slice(0,8)}${m.already_synced ? " (já sync)" : ""}`,
                    no_lead_with_this_email: "Sem lead com este email",
                    self: "Nosso envio",
                    no_sender: "Sem remetente",
                  }[m.verdict] || m.verdict;
                  return (
                    <div key={i} data-testid={`diag-msg-${i}`} className="grid grid-cols-[110px_1fr_120px_120px] gap-2 items-center px-3 py-2 border-b border-border last:border-0 bg-card hover:bg-muted/40">
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {m.received_at ? new Date(m.received_at).toLocaleString("pt-PT", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">{m.sender || "(sem remetente)"}</div>
                        <div className="truncate text-[11px] text-muted-foreground">{m.subject}</div>
                      </div>
                      <div className={`text-[10px] font-mono ${m.is_read ? "text-muted-foreground" : "text-[hsl(var(--success))] font-bold"}`}>
                        {m.is_read ? "lido" : "por ler"}
                      </div>
                      <div className={`text-[10px] rounded-full px-2 py-0.5 border truncate ${verdictStyle}`} title={verdictLabel}>
                        {verdictLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-5"><Mail className="w-4 h-4 text-foreground" /><h3 className="font-ui text-lg">Emails enviados / registados</h3></div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {logs.length === 0 && <div className="text-sm text-muted-foreground/70">Sem emails.</div>}
          {logs.map((l) => {
            // 25/02/2026 — Guards defensivos: em produção alguns registos legado
            // tinham `status` nulo, o que provocava `l.status.toUpperCase()`
            // TypeError e crashava toda a página (ecrã azul sem informação).
            const status = l?.status || "unknown";
            const statusClass = status === "sent" ? "text-emerald-400"
              : status === "error" ? "text-rose-400"
              : "text-amber-300";
            let when = "—";
            try {
              if (l?.created_at) when = new Date(l.created_at).toLocaleString("pt-PT");
            } catch { /* keep dash */ }
            return (
            <div key={l?.id || Math.random()} className="border border-border rounded-lg p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className={`font-mono ${statusClass}`}>{status.toUpperCase()}</span>
                <span className="text-muted-foreground">{when}</span>
              </div>
              <div className="text-foreground/80">{l?.subject || "(sem assunto)"}</div>
              <div className="text-muted-foreground">Para: {l?.to || "—"}</div>
              {l?.error && <div className="text-rose-300/70 mt-1 font-mono text-[11px]">{l.error}</div>}
            </div>
            );
          })}
        </div>
      </div>

      {/* RT Readiness Check Panel — verificar licença Microsoft + activar cronjob */}
      <RTReadinessPanel />
    </div>
  );
}
